import Header from "../../components/Header";
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { tokens } from "../../theme";
import {
  DownloadOutlined,
  PointOfSale,
  PersonAdd,
  Traffic,
} from "@mui/icons-material";
import LineChart from "../../components/LineChart";
import BarChart from "../../components/BarChart";
import GeoChart from "../../components/GeoChart";
import StatBox from "../../components/StatBox";
import ProgressCircle from "../../components/ProgressCircle";
import { useGetDashboardMutation } from "../../features/auth/apiSlice";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectSponserID,
  setNotifications,
} from "../../features/auth/authSlice";

const Index = () => {
  const [getDashboard] = useGetDashboardMutation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const sponserId = useSelector(selectSponserID);
  const dispatch = useDispatch();
  const [data, setData] = useState({} as any);
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [lineData, setLineData] = useState([] as any);
  const [geoData, setGeoData] = useState([] as any);
  const [barData, setBarData] = useState([] as any);

  useEffect(() => {
    const getDashboardFn = async () => {
      const response: any = await getDashboard({ sponserId }).unwrap();
      const modifiedResponse = [
        {
          id: "transactions",
          color: tokens(theme.palette.mode).greenAccent[500],
          data: response.transactions.map((transaction: any) => {
            return {
              x: new Date(transaction.createdAt).toISOString().substring(0, 10),
              y: transaction.total_amount,
            };
          }),
        },
      ];
      const geoData = response.hitCounts.map((hit: any) => {
        return { id: hit.id, value: hit.value };
      });

      setBarData(response?.barData);
      setGeoData(geoData);
      setLineData(modifiedResponse);
      setData(response);
      dispatch(setNotifications({ notifications: response?.notifications }));
    };
    getDashboardFn();
  }, [dispatch, getDashboard, sponserId, theme.palette.mode]);

  return (
    <div className="m-[20px] mt-[60px]">
      <div className="flex justify-between items-center">
        <Header title="DASHBOARD" subtitle="Welcome to your dashboard" />
      </div>
      {/* GRID AND CHARTS */}
      <Box
        display={isNonMobile ? "grid" : "bolck"}
        gridTemplateColumns={"repeat(12,1fr)"}
        gridAutoRows="140px"
        gap="8px"
      >
        {/* ROW 1 */}
        <Box
          gridColumn={"span 3"}
          bgcolor={
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : colors.primary[900]
          }
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius={5}
        >
          <StatBox
            title={data?.transactions?.length}
            subtitle="Total transactions"
            progress={0.01 * data?.transactions?.length}
            increase={`+${data?.transactions?.length / 100}%`}
            icon={
              <PointOfSale
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
        <Box
          gridColumn={"span 3"}
          bgcolor={
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : colors.primary[900]
          }
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius={5}
        >
          <StatBox
            title={data?.totalHits ? data?.totalHits.toString() : "0"}
            subtitle="Number of clicks"
            progress={0.01 * data?.totalHits}
            increase={`+${data?.totalHits / 100}%`}
            icon={
              <Traffic
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
        <Box
          gridColumn={"span 3"}
          bgcolor={
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : colors.primary[900]
          }
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius={5}
        >
          <StatBox
            title={data?.totalCreators}
            subtitle="Total Creators"
            progress={0.01 * data?.totalCreators}
            increase={`+${data?.totalCreators / 100}%`}
            icon={
              <PersonAdd
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
        <Box
          gridColumn={"span 3"}
          bgcolor={
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : colors.primary[900]
          }
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius={5}
        >
          <StatBox
            title={data?.transactions?.length + data?.totalHits}
            subtitle="Traffic Inbound"
            progress={data?.transactions?.length + data?.totalHits}
            increase={`+${data?.transactions?.length + data?.totalHits / 100}%`}
            icon={
              <Traffic
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        {/* ROW 2 */}
        <Box
          gridColumn={"span 8"}
          gridRow="span 2"
          bgcolor={
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : colors.primary[900]
          }
          borderRadius={5}
        >
          <Box
            mt="25px"
            p="0 30px"
            display="flex"
            justifyContent="space-between"
            borderRadius={5}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight={"600"}
                color={colors.grey[100]}
              >
                Total Spend
              </Typography>
              <Typography
                variant="h3"
                fontWeight={"bold"}
                color={colors.greenAccent[500]}
              >
                {"Rs. " + data?.totalSpend}
              </Typography>
            </Box>

            <Box>
              <IconButton>
                <DownloadOutlined
                  sx={{ fontSize: "26px", color: colors.greenAccent[500] }}
                />
              </IconButton>
            </Box>
          </Box>
          <Box height="250px" mt="-20px">
            <LineChart isDashboard={true} data={lineData} />
          </Box>
        </Box>

        {/* TRANSACTION */}
        <Box
          gridColumn={"span 4"}
          gridRow="span 2"
          bgcolor={
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : colors.primary[900]
          }
          overflow="auto"
          borderRadius={5}
        >
          <Box
            display="flex"
            justifyContent={"space-between"}
            alignItems="center"
            borderBottom={`4px solid ${colors.primary[500]}`}
            color={colors.grey[100]}
            p="15px"
          >
            <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
              Recent Transactions
            </Typography>
          </Box>
          {data?.transactions?.map((transaction: any, i: number) => (
            <>
              {transaction.status === "done" && (
                <Box
                  key={`${transaction.feedId}-${i}`}
                  display="flex"
                  justifyContent={"space-between"}
                  alignItems="center"
                  borderBottom={`4px solid ${colors.primary[500]}`}
                  p="15px"
                >
                  <Box>
                    <Typography
                      color={colors.greenAccent[500]}
                      variant="h5"
                      fontWeight="600"
                    >
                      {transaction._id}
                    </Typography>
                  </Box>

                  <Box color={colors.grey[100]}>
                    {new Date(transaction.createdAt)
                      .toISOString()
                      .substring(0, 10)}
                  </Box>
                  <Box
                    bgcolor={colors.greenAccent[500]}
                    p="5px 10px"
                    borderRadius={"4px"}
                  >
                    {transaction.total_amount}
                  </Box>
                </Box>
              )}
            </>
          ))}
        </Box>

        {/* ROW 3 */}
        <Box
          gridColumn={"span 4"}
          gridRow="span 2"
          bgcolor={
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : colors.primary[900]
          }
          p="30px"
          sx={{ marginBottom: "4px" }}
          borderRadius={5}
        >
          <Typography variant="h5" fontWeight="600">
            Campaign
          </Typography>
          <Box
            display={"flex"}
            flexDirection="column"
            alignItems="center"
            mt="25px"
          >
            <ProgressCircle size="125" />
            <Typography
              variant="h5"
              color={colors.greenAccent[500]}
              sx={{ mt: "15px" }}
            >
              {data?.totalSpend + " Rs"} Total Spend
            </Typography>
          </Box>
        </Box>

        <Box
          gridColumn={"span 4"}
          gridRow="span 2"
          bgcolor={
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : colors.primary[900]
          }
          sx={{ marginBottom: "4px" }}
          borderRadius={5}
        >
          <Typography
            variant="h5"
            fontWeight="600"
            sx={{ p: "30px 30px 0 30px" }}
          >
            Sales Quantity
          </Typography>
          <Box height={"250px"} mt="-20px">
            <BarChart isDashboard={true} data={barData} />
          </Box>
        </Box>

        <Box
          gridColumn={"span 4"}
          gridRow="span 2"
          bgcolor={
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : colors.primary[900]
          }
          p="30px"
          sx={{ marginBottom: "4px" }}
          borderRadius={5}
        >
          <Typography variant="h5" fontWeight="600" sx={{ mb: "15px" }}>
            Geography Based Traffic
          </Typography>
          <Box height={"200px"}>
            <GeoChart isDashboard={true} data={geoData} />
          </Box>
        </Box>
        {/*  */}
      </Box>
    </div>
  );
};

export default Index;
